<template>
  <div class="entry-detail w-full">
    <wrapper :project="project">
      <article>
        <div>
          <!-- this extra div has to be here, if not the edit toggle wont re-open after save -->
          <!-- Description list -->
          <div class="relative mx-auto mt-6 max-w-5xl px-4 sm:px-6 lg:px-8">
            <div class="absolute right-8 top-0">
              <button
                v-if="!editing"
                type="button"
                class="inline-flex items-center justify-center rounded-md border border-warning bg-warning px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-warning-dark focus:outline-none focus:ring-2 focus:ring-yellow-700 focus:ring-offset-2"
                @click="editing = true">
                <svg
                  class="-ml-1 mr-2 size-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Edit</span>
              </button>
            </div>
            <div class="mt-1 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div
                v-if="editing"
                class="sm:col-span-2">
                <dt class="text-sm font-medium text-font">
                  Name
                </dt>
                <dd class="mt-1 text-sm text-font-darkest">
                  <input
                    id="name"
                    v-model="form.name"
                    :class="{'border-red-300': form.errors.name }"
                    type="text"
                    name="name"
                    class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm">
                  <p
                    v-if="form.errors.name"
                    class="absolute -ml-3 mt-2 text-xs text-red-600">
                    {{ form.errors.name }}
                  </p>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Client
                </dt>
                <dd class="mt-1 text-sm text-font-darkest">
                  <select
                    v-if="editing"
                    id="clientId"
                    v-model="form.client_id"
                    class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm">
                    <option
                      v-for="option in clients"
                      :value="option.id">
                      {{ option.name }}
                    </option>
                  </select>
                  <p v-else-if="project.client !== null">
                    {{ project.client.name }}
                  </p>
                  <i
                    v-else
                    class="text-font">Not defined</i>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Project manager
                </dt>
                <dd class="mt-1 text-sm text-font-darkest">
                  <select
                    v-if="editing"
                    id="kam"
                    v-model="form.project_manager"
                    class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm">
                    <option
                      v-for="option in team_members"
                      :value="option.value">
                      {{ option.text }}
                    </option>
                  </select>
                  <p v-else-if="project.manager !== null">
                    {{ project.manager.name }}
                  </p>
                  <i
                    v-else
                    class="text-font">Not defined</i>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Contact
                </dt>
                <dd class="mt-1 text-sm text-font-darkest">
                  <select
                    v-if="editing"
                    id="clientName"
                    v-model="form.client_contact_id"
                    class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm">
                    <option
                      v-for="option in project.client.contacts"
                      :value="option.id">
                      {{ option.name }}
                    </option>
                  </select>
                  <p v-else-if="project.contact !== null">
                    {{ project.contact.name }}
                  </p>
                  <i
                    v-else
                    class="text-font">Not defined</i>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Status:
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <!-- Class list is listed in StatusBadge.vue -->
                  <StatusBadge :status="project.status" />
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Number of travelers
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <div v-if="editing">
                    <input
                      id="travelers"
                      v-model="form.total_travelers"
                      name="travelers"
                      type="text"
                      class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm"
                      placeholder="Travelers">
                  </div>
                  <p
                    v-else-if="form.total_travelers !== null"
                    class="mt-1 text-sm text-gray-900">
                    {{ form.total_travelers }}
                  </p>
                  <p
                    v-else
                    class="text-gray-400">
                    No travelers added
                  </p>
                </dd>
                <dt class="mt-2 text-sm font-medium text-font">
                  Budget per person
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <div v-if="editing">
                    <input
                      id="budget"
                      v-model="form.budget_per_traveller"
                      name="budget"
                      type="text"
                      class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm"
                      placeholder="Budget">
                  </div>
                  <p
                    v-else-if="form.budget_per_traveller !== null"
                    class="mt-1 text-sm text-gray-900">
                    {{ form.budget_per_traveller }}
                  </p>
                  <p
                    v-else
                    class="text-gray-400">
                    No budget added
                  </p>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Created
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <div
                    v-if="project.created_at !== null"
                    class="mt-1 text-sm text-gray-900">
                    <p class="mb-2">
                      <DateTimeFormatter
                        date-time-style="tiny"
                        :date-time="project.created_at" />
                    </p>
                  </div>
                  <p
                    v-else
                    class="text-gray-400">
                    No date added
                  </p>
                </dd>
                <dt class="text-sm font-medium text-font">
                  Last Updated
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <div
                    v-if="project.updated_at !== null"
                    class="mt-1 text-sm text-gray-900">
                    <p class="mb-2">
                      <DateTimeFormatter
                        date-time-style="tiny"
                        :date-time="project.updated_at" />
                    </p>
                  </div>
                  <p
                    v-else
                    class="text-gray-400">
                    No date added
                  </p>
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-font">
                  Project Comment
                </dt>
                <dd class="mt-1 max-w-prose text-sm">
                  <div v-if="editing">
                    <textarea
                      id="about"
                      v-model="form.comment"
                      name="about"
                      rows="4"
                      class="block w-full rounded-md border border-font-lightest p-1 text-sm text-font-darkest shadow-sm focus:border-primary focus:ring-primary"
                      placeholder="Add your comment..." />
                  </div>
                  <p
                    v-else-if="form.comment !== null"
                    class="text-font-darkest">
                    {{ form.comment }}
                  </p>
                  <p
                    v-else
                    class="text-gray-400">
                    No comment added
                  </p>
                </dd>
              </div>
            </div>
            <div
              v-if="editing"
              class="mt-4 flex w-full flex-col justify-between space-y-1 sm:flex-row sm:space-x-1 sm:space-y-0">
              <div class="flex w-32 justify-between">
                <button
                  v-if="saving"
                  type="button"
                  class="inline-flex justify-center rounded-md border border-green-100 bg-success px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-success-dark focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2">
                  <svg
                    class="-mx-1 size-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <button
                  v-else
                  type="button"
                  class="inline-flex justify-center rounded-md border border-green-200 bg-success px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-success-dark focus:outline-none focus:ring-2 focus:ring-success-dark focus:ring-offset-2"
                  @click="save">
                  Save
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-red-200 bg-warning px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-warning-dark focus:outline-none focus:ring-2 focus:ring-warning-dark focus:ring-offset-2"
                  @click="cancelEdit">
                  Cancel
                </button>
              </div>
              <div>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-red-200 bg-danger px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-danger-dark focus:outline-none focus:ring-2 focus:ring-danger-dark focus:ring-offset-2"
                  @click="showDeleteFrom = !showDeleteFrom">
                  Delete project
                </button>
              </div>
            </div>
          </div>

          <!-- Client feed -->
          <div class="mx-auto mt-8 max-w-5xl px-4 pb-12 sm:px-6 lg:px-8">
            <div class="mt-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <h2 class="text-sm font-medium text-gray-500">
                Proposals in project
              </h2>
              <h2 class="text-right text-sm font-medium text-gray-500">
                Recent activity
              </h2>
            </div>

            <div class="mt-1 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <!-- Project proposals -->
              <SimpleCardsWithStatus
                :items="proposals"
                route-name="proposals.show"
                :settings-menu="[
                  { text: 'Delete Proposal', action: 'delete' },
                  {
                    text: ['Hide Proposal', 'Unhide Proposal'],
                    action: ['hide', 'unhide'],
                    dynamicProperty: 'visible',
                  },
                  { text: 'Go to Proposal', action: 'view' },
                ]"
                @settings-menu-click="(e) => handleProposalClick(e)" />
              <!-- Project activity -->
              <div class="mt-3 flex flex-1 grow">
                <ul v-if="activities">
                  <li v-for="(activity,key) in activities">
                    <div class="relative pb-8">
                      <span
                        v-if="key < activities.length-1"
                        class="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true" />
                      <div class="relative flex items-start space-x-3">
                        <div class="relative">
                          <img
                            class="flex size-10 items-center justify-center rounded-full bg-gray-400 object-cover ring-8 ring-white"
                            :src="activity.creator.profile_photo_url"
                            :alt="activity.creator.name">

                          <span class="absolute -bottom-0.5 -right-1 rounded-tl bg-white px-0.5 py-px">
                            <ActivityIcon
                              :type="activity.type"
                              class="text-font-darkest" />
                          </span>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div>
                            <div class="text-sm">
                              <p
                                class="font-medium text-font-darkest">
                                {{ activity.creator.name }}
                              </p><span
                                v-if="activity.contact !== null"
                                class="text-xs text-font"> with <p
                                class="font-medium text-font-darkest">{{ activity.contact.name }}</p></span>
                            </div>
                            <p class="mt-0.5 text-sm text-font">
                              <DateTimeFormatter :date-time="activity.created_at" />
                            </p>
                          </div>
                          <div class="mt-2 text-sm text-gray-700">
                            <p>
                              {{ activity.comment }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <div
                  v-else
                  class="flex flex-1 grow items-center justify-between truncate rounded-md border border-gray-100 bg-white">
                  <div class="flex-1 truncate px-4 py-2 text-center text-sm">
                    <p class="font-medium text-font-lightest">
                      No activities in this project yet
                    </p>
                  </div>
                </div>
                <Link
                  v-if="activities?.length > 5"
                  :href="route('project.activities', {project: project})"
                  class="block w-full rounded-md border border-font-darkest p-2 text-center text-font-darkest">
                  See all
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </wrapper>
  </div>
  <Modal
    :show="showDeleteFrom"
    max-width="md"
    :closeable="true"
    @close="showDeleteFrom = false">
    <DeleteConfirmation
      :project="project"
      @close="showDeleteFrom = false" />
  </Modal>
</template>

<script>
import { Link } from '@inertiajs/vue3';

import SimpleCardsWithStatus from '@/Components/Cards/SimpleCardsWithStatus.vue';
import DateTimeFormatter from '@/Components/DateTimeFormatter.vue';
import ActivityIcon from '@/Components/ActivityIcon.vue';

import Modal from '../Jetstream/Modal.vue';
import DeleteConfirmation from './Modal/DeleteConfirmation.vue';
import StatusBadge from './StatusBadge.vue';
import Wrapper from '../Pages/Project/Wrapper.vue';
import '@vuepic/vue-datepicker/dist/main.css';
import { notify } from 'notiwind';

export default {
  name: 'ProjectDetail',
  components: {
    ActivityIcon,
    Link,
    DateTimeFormatter,
    SimpleCardsWithStatus,
    Wrapper,
    StatusBadge,
    Modal,
    DeleteConfirmation,
  },

  props: {
    project: Object,
    activities: Array,
    client: Object,
    clients: {
      type: Array,
      required: true,
    },

    availableStatuses: {
      type: Array, // or the appropriate type
      default: () => [], // or the appropriate default value
    },
  },

  data() {
    return {
      showDeleteFrom: false,
      editing: false,
      saving: false,
      team_members: [],

      form: this.$inertia.form({
        id: this.project.id,
        client_id: this.project.client_id,
        name: this.project.name,
        status: this.project.status,
        comment: this.project.comment,
        total_travelers: this.project.total_travelers,
        budget_per_traveller: this.project.budget_per_traveller,
        project_manager: this.project.project_manager,
        client_contact_id: this.project.client_contact_id,
      }),

      proposals: this.project.proposals,
    };
  },

  computed: {
    recentlySuccessful() {
      return this.form.recentlySuccessful;
    },
  },

  watch: {
    recentlySuccessful(val, oldVal) {
      if (val === true) {
        this.editing = false;
      }
    },
  },

  created() {
    this.team_members.push({
      value: this.$page.props.team.owner.id, text: this.$page.props.team.owner.name,
    });
    for (let i = 0; i < this.$page.props.team.users.length; i++) {
      this.team_members.push({
        value: this.$page.props.team.users[i].id, text: this.$page.props.team.users[i].name,
      });
    }
  },

  methods: {
    cancelEdit() {
      this.editing = false;
      this.form.name = this.project.name;
      this.form.comment = this.project.comment;
      this.form.created_by = this.project.created_by;
      this.form.clearErrors();
    },

    async handleProposalClick(e) {
      try {
        switch (e.action) {
        case 'view':
          this.$inertia.visit(route('proposals.show', { proposal: e.id }));
          break;

        case 'hide':
        case 'unhide': {
          await axios.put(route('api.proposal.hide', { proposal: e.id }), {
            id: e.id,
            action: e.action,
          });
          const index = this.proposals.findIndex((item) => item.id === e.id);
          if (index !== -1) {
            this.proposals[index].visible = e.action !== 'hide';
          }
          notify({
            type: 'success',
            title: e.action === 'hide' ? 'Proposal hidden' : 'Proposal unhidden',
          }, 4000);
          break;
        }

        case 'delete': {
          await axios.delete(route('api.proposal.destroy', { proposal: e.id }));
          const index = this.proposals.findIndex((item) => item.id === e.id);
          if (index !== -1) {
            this.proposals.splice(index, 1);
          }
          notify({
            type: 'success',
            title: 'Proposal deleted',
          }, 4000);
          break;
        }

        default:
          console.warn(`Unhandled action: ${e.action}`);
        }
      } catch (error) {
        if (e.action === 'delete') {
          notify({
            type: 'danger',
            title: 'Failed to delete proposal',
            text: 'Could not delete proposal',
          }, 4000);
        } else if (e.action === 'hide' || e.action === 'unhide') {
          notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not change the visibility status of the proposal',
          }, 4000);
        }
        console.error(error);
      }
    },

    save() {
      this.saving = true;
      this.form.put(route('projects.update', { project: this.project.id }), {
        errorBag: 'updateProject',
        preserveScroll: true,
        onSuccess: () => {
          this.$notify({
            type: 'success',
            title: 'Updated',
            text: 'The project was updated',
          }, 4000);
        },
        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not update the project',
          }, 4000);
        },
      });
      this.saving = false;
      !this.form.name ? this.editing = true : this.editing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
//
</style>
